import React, { useEffect, useRef, useState } from 'react';
import { FiCheck, FiChevronRight, FiCpu } from 'react-icons/fi';
import { IoAlertCircle, IoChevronUp } from 'react-icons/io5';

// One control for the chat model and its effort. The list shows every model
// the user's level may pick (set in Admin → AI Models). Hovering a model
// opens a submenu on its right, flush with the row, with the efforts that
// model accepts. A click opens it where there is no hover (touch); clicking
// a model whose submenu is open picks it at its default effort.
// The button reads
// "Model · Effort". A model that cannot run yet (its connection is not set
// up) is shown greyed out with the reason.

const EFFORT_LABELS = {
  low: 'Low',
  medium: 'Medium',
  high: 'High',
  xhigh: 'Extra high',
  max: 'Max',
};

const ModelEffortPicker = ({
  userInfo,
  currentModel, // picked model name; null = the level's default
  effort, // picked effort; null = the model's own default
  onPick, // (modelName | null, effort | null)
  disabled,
  openTo = 'up',
}) => {
  const [open, setOpen] = useState(false);
  const [hovered, setHovered] = useState(null);
  // Switching to another row waits a moment while a submenu is open, so a
  // diagonal move from a row into its submenu does not jump to the row it
  // crosses; reaching the submenu cancels the switch.
  const switchTimer = useRef(null);
  const hoverRow = (name) => {
    clearTimeout(switchTimer.current);
    if (!hovered || hovered === name) setHovered(name);
    else switchTimer.current = setTimeout(() => setHovered(name), 200);
  };
  const keepSubmenu = () => clearTimeout(switchTimer.current);
  useEffect(() => () => clearTimeout(switchTimer.current), []);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => !ref.current?.contains(e.target) && setOpen(false);
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  const models = userInfo?.available_models || [];
  const fallback = userInfo?.default_model;
  if (!models.length && !fallback) return null;

  const selected =
    models.find((m) => m.name === currentModel && m.available !== false) ||
    fallback;
  const selectedEffort = selected?.efforts?.includes(effort)
    ? effort
    : selected?.effort || null;
  const noneReady = !models.some((m) => m.available !== false);

  const pick = (model, level) => {
    onPick(model.name === fallback?.name ? null : model.name, level);
    setOpen(false);
    setHovered(null);
  };

  const label = noneReady
    ? 'AI not set up'
    : `${selected?.name || 'Model'}${
        selectedEffort
          ? ` · ${EFFORT_LABELS[selectedEffort] || selectedEffort}`
          : ''
      }`;

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        title={
          noneReady
            ? 'An admin can finish this in Admin → AI Models'
            : 'Model and effort'
        }
        className={`flex h-7 items-center gap-1.5 rounded-full border px-2.5 text-xs transition-all duration-300 disabled:pointer-events-none disabled:opacity-50 ${
          noneReady
            ? 'border-amber-600/60 text-amber-400'
            : 'border-zinc-600 hover:bg-zinc-600 hover:text-white'
        }`}
      >
        {noneReady ? <IoAlertCircle /> : <FiCpu />}
        <span className="max-w-[16rem] truncate">{label}</span>
        <IoChevronUp
          className={`transition-transform ${open ? 'rotate-0' : 'rotate-180'}`}
        />
      </button>

      {open && (
        <div
          className={`absolute left-0 z-50 w-72 rounded-lg border border-zinc-700 bg-zinc-900 shadow-lg ${
            openTo === 'up' ? 'bottom-full mb-2' : 'top-full mt-2'
          }`}
        >
          {noneReady && (
            <p className="border-b border-zinc-800 px-4 py-2.5 text-xs text-amber-400">
              AI models aren&apos;t set up yet. An admin can finish this in
              Admin → AI Models.
            </p>
          )}
          <ul className="py-1">
            {models.map((model) => {
              const ready = model.available !== false;
              const isSelected = ready && selected?.name === model.name;
              const efforts = model.efforts || [];
              return (
                <li
                  key={model.name}
                  className="relative"
                  onMouseEnter={() => ready && hoverRow(model.name)}
                >
                  <button
                    type="button"
                    disabled={!ready}
                    title={ready ? model.description : model.reason}
                    onClick={() =>
                      // Open its efforts first (touch has no hover); once
                      // open, clicking the model again picks its default.
                      efforts.length && hovered !== model.name
                        ? setHovered(model.name)
                        : pick(model, model.effort || null)
                    }
                    className={`flex w-full items-center justify-between gap-2 px-4 py-2.5 text-left transition-colors ${
                      ready
                        ? 'hover:bg-zinc-800'
                        : 'cursor-not-allowed opacity-50'
                    } ${hovered === model.name ? 'bg-zinc-800' : ''} ${
                      isSelected ? 'text-blue-400' : 'text-zinc-200'
                    }`}
                  >
                    <span className="flex flex-col">
                      <span className="text-xs font-semibold">
                        {model.name}
                      </span>
                      <span className="mt-0.5 text-xs text-zinc-400">
                        {ready
                          ? model.description
                          : 'Not set up yet — an admin can finish it'}
                      </span>
                    </span>
                    <span className="flex shrink-0 items-center gap-1.5">
                      {isSelected && <FiCheck />}
                      {ready && efforts.length > 0 && (
                        <FiChevronRight className="text-zinc-500" />
                      )}
                    </span>
                  </button>

                  {hovered === model.name && efforts.length > 0 && (
                    <ul
                      onMouseEnter={keepSubmenu}
                      className="absolute left-full top-0 z-50 w-40 rounded-lg border border-zinc-700 bg-zinc-900 py-1 shadow-lg"
                    >
                      <li className="px-3 pb-1 pt-1.5 text-[11px] uppercase tracking-wide text-zinc-500">
                        Effort
                      </li>
                      {efforts.map((level) => {
                        const chosen = isSelected && selectedEffort === level;
                        return (
                          <li key={level}>
                            <button
                              type="button"
                              onClick={() => pick(model, level)}
                              className={`flex w-full items-center justify-between px-3 py-2 text-left text-xs hover:bg-zinc-800 ${
                                chosen ? 'text-blue-400' : 'text-zinc-200'
                              }`}
                            >
                              {EFFORT_LABELS[level] || level}
                              {level === model.effort && !chosen && (
                                <span className="text-[10px] text-zinc-500">
                                  default
                                </span>
                              )}
                              {chosen && <FiCheck />}
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
};

export default ModelEffortPicker;
